import { useEffect, useState } from "react";
import styled from "styled-components";
import {
  Box,
  Checkbox,
  FormControlLabel,
  FormHelperText,
  MenuItem,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@mui/material";
import useOverlayTemplates from "../../hooks/useOverlayTemplates";
import useLanguage from "../../hooks/useLanguage";

const Section = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const FieldRow = styled(Box)`
  display: flex;
  gap: 12px;
`;

const DEFAULT_TIMING = { mode: "full" };

function computeValid(templateId, textOverrides, textElements, timing) {
  if (!templateId) return false;
  for (const el of textElements) {
    if (!(textOverrides[el.id] || "").trim()) return false;
  }
  if (timing.mode === "fromStart") {
    if (!timing.end || timing.end <= 0) return false;
  }
  if (timing.mode === "range") {
    if (
      timing.start == null ||
      timing.end == null ||
      timing.start < 0 ||
      timing.end <= timing.start
    )
      return false;
  }
  return true;
}

export default function OverlayPicker({ value, onChange, error }) {
  const [{ pages: { overlayPicker: t } }] = useLanguage();
  const { list } = useOverlayTemplates();

  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [textOverrides, setTextOverrides] = useState({});
  const [timing, setTiming] = useState(DEFAULT_TIMING);
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    list()
      .then((data) => setTemplates(Array.isArray(data) ? data : []))
      .catch(() => setTemplates([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const enabled = value?.enabled ?? false;

  const textElements = selectedTemplate
    ? selectedTemplate.elements.filter((el) => el.type === "text")
    : [];

  const emit = (tpl, overrides, tim, isEnabled) => {
    if (!isEnabled) {
      onChange({ enabled: false, payload: null, valid: true });
      return;
    }
    const valid = computeValid(tpl?.id ?? null, overrides, tpl ? tpl.elements.filter((e) => e.type === "text") : [], tim);
    onChange({
      enabled: true,
      payload: tpl
        ? { templateId: tpl.id, textOverrides: overrides, timing: tim }
        : null,
      valid,
    });
  };

  const handleToggle = (e) => {
    const next = e.target.checked;
    setTouched(false);
    emit(selectedTemplate, textOverrides, timing, next);
  };

  const handleTemplateChange = (e) => {
    const tpl = templates.find((t) => t.id === e.target.value) || null;
    setSelectedTemplate(tpl);
    const overrides = {};
    if (tpl) {
      tpl.elements.filter((el) => el.type === "text").forEach((el) => {
        overrides[el.id] = el.defaultText || "";
      });
    }
    setTextOverrides(overrides);
    setTouched(false);
    emit(tpl, overrides, timing, true);
  };

  const handleTextChange = (id, val) => {
    const next = { ...textOverrides, [id]: val };
    setTextOverrides(next);
    setTouched(true);
    emit(selectedTemplate, next, timing, true);
  };

  const handleTimingMode = (e) => {
    const mode = e.target.value;
    let next;
    if (mode === "full") next = { mode: "full" };
    else if (mode === "fromStart") next = { mode: "fromStart", end: "" };
    else next = { mode: "range", start: "", end: "" };
    setTiming(next);
    emit(selectedTemplate, textOverrides, next, true);
  };

  const handleTimingField = (field, val) => {
    const parsed = val === "" ? "" : Number(val);
    const next = { ...timing, [field]: parsed };
    setTiming(next);
    emit(selectedTemplate, textOverrides, next, true);
  };

  return (
    <Section>
      <FormControlLabel
        control={<Checkbox checked={enabled} onChange={handleToggle} />}
        label={t.enable}
      />

      {enabled && (
        <>
          <TextField
            select
            label={t.selectTemplate}
            value={selectedTemplate?.id || ""}
            onChange={handleTemplateChange}
            fullWidth
            disabled={templates.length === 0}
            helperText={templates.length === 0 ? t.noTemplates : undefined}
          >
            {templates.map((tpl) => (
              <MenuItem key={tpl.id} value={tpl.id}>
                {tpl.name}
              </MenuItem>
            ))}
          </TextField>

          {selectedTemplate && textElements.length > 0 && (
            <>
              <Typography variant="body2" color="text.secondary">
                {t.texts}
              </Typography>
              {textElements.map((el) => {
                const empty = touched && !(textOverrides[el.id] || "").trim();
                return (
                  <TextField
                    key={el.id}
                    label={el.label}
                    value={textOverrides[el.id] ?? ""}
                    onChange={(e) => handleTextChange(el.id, e.target.value)}
                    inputProps={{ dir: "auto" }}
                    fullWidth
                    error={empty}
                    helperText={empty ? t.required : undefined}
                  />
                );
              })}
            </>
          )}

          {selectedTemplate && (
            <>
              <Typography variant="body2" color="text.secondary">
                {t.timing}
              </Typography>
              <RadioGroup value={timing.mode} onChange={handleTimingMode}>
                <FormControlLabel
                  value="full"
                  control={<Radio size="small" />}
                  label={t.timingFull}
                />
                <FormControlLabel
                  value="fromStart"
                  control={<Radio size="small" />}
                  label={t.timingFromStart}
                />
                {timing.mode === "fromStart" && (
                  <TextField
                    label={t.end}
                    type="number"
                    size="small"
                    value={timing.end}
                    onChange={(e) => handleTimingField("end", e.target.value)}
                    inputProps={{ min: 1 }}
                    sx={{ ml: 4, mt: 0.5, width: 160 }}
                  />
                )}
                <FormControlLabel
                  value="range"
                  control={<Radio size="small" />}
                  label={t.timingRange}
                />
                {timing.mode === "range" && (
                  <FieldRow sx={{ ml: 4, mt: 0.5 }}>
                    <TextField
                      label={t.start}
                      type="number"
                      size="small"
                      value={timing.start}
                      onChange={(e) => handleTimingField("start", e.target.value)}
                      inputProps={{ min: 0 }}
                      sx={{ width: 140 }}
                    />
                    <TextField
                      label={t.end}
                      type="number"
                      size="small"
                      value={timing.end}
                      onChange={(e) => handleTimingField("end", e.target.value)}
                      inputProps={{ min: 0 }}
                      sx={{ width: 140 }}
                    />
                  </FieldRow>
                )}
              </RadioGroup>
            </>
          )}

          {error && <FormHelperText error>{error}</FormHelperText>}
        </>
      )}
    </Section>
  );
}
